import { FormattedMessage, useIntl } from "react-intl";

import Apps from "constants/applications";
import {
  PROOF_TRANSACTIONS,
  LENDER_ESIGN,
  HIDE_ESIGN_FROM_NEW_TRANSACTION_DROPDOWN,
  HIDE_NOTARIZATION_FROM_NEW_TRANSACTION_DROPDOWN,
  IDENTIFY_TRANSACTIONS,
  HIDE_CLOSE_FROM_NEW_TRANSACTION_DROPDOWN,
} from "constants/feature_gates";
import {
  ESIGN_TRANSACTION_TYPE,
  IDENTIFY_TRANSACTION_TYPE,
  VERIFY_TRANSACTION_TYPE,
} from "constants/transaction";
import Button from "common/core/button";
import PopoutMenu from "common/core/popout_menu";
import { PopoutMenuMultilineItem } from "common/core/popout_menu/multiline";
import { useFeatureFlag } from "common/feature_gating";
import { TRANSACTION_CTA_MESSAGES } from "common/dashboard/constants";
import { DashboardDropdownIcon } from "common/dashboard/dropdown";
import { useCreateRealEstateTransaction } from "common/transactions/create/hooks/use-create-real-estate-transaction";
import {
  REAL_ESTATE_IDENTIFY_CONFIG,
  REAL_ESTATE_SIGN_CONFIG,
  REAL_ESTATE_VERIFY_CONFIG,
  OTHER_CONFIG,
} from "common/transaction_creation/v3/config";

import Styles from "./index.module.scss";

type Props = {
  showPlaceOrderUI: boolean;
  handleRealEstateOption: () => void;
  orgIsActive?: boolean;
  portal: "title_agency" | "lender";
  recipient?: NonNullable<Parameters<typeof useCreateRealEstateTransaction>[0]>["recipient"];
  showVerifyOption?: boolean;
};

export function NewRealEstateTransactionMenu({
  showPlaceOrderUI,
  handleRealEstateOption,
  orgIsActive = true,
  portal,
  recipient,
  showVerifyOption,
}: Props) {
  const showCloseOption = !useFeatureFlag(HIDE_CLOSE_FROM_NEW_TRANSACTION_DROPDOWN);
  const lenderEsignEnabled = useFeatureFlag(LENDER_ESIGN);
  const showEsignOption = !useFeatureFlag(HIDE_ESIGN_FROM_NEW_TRANSACTION_DROPDOWN);
  const showNotarizationOption = !useFeatureFlag(HIDE_NOTARIZATION_FROM_NEW_TRANSACTION_DROPDOWN);

  const isProofEnabled = useFeatureFlag(PROOF_TRANSACTIONS);
  const showIdentifyOption = useFeatureFlag(IDENTIFY_TRANSACTIONS) && isProofEnabled;

  const intl = useIntl();

  const isTitlePortal = portal === Apps.TITLE_AGENCY;
  const realClosingContent = isTitlePortal
    ? intl.formatMessage(TRANSACTION_CTA_MESSAGES.titleCloseSubText)
    : intl.formatMessage(TRANSACTION_CTA_MESSAGES.lenderCloseSubText);

  const { createTransaction, loading } = useCreateRealEstateTransaction({ recipient });

  if (!showCloseOption && !showEsignOption && !showNotarizationOption && !showIdentifyOption) {
    return null;
  }

  return (
    <PopoutMenu
      placement="bottomRight"
      className={Styles.dropdownButton}
      target={
        <Button
          disabled={!orgIsActive}
          isLoading={loading}
          disabledHint={
            !orgIsActive && (
              <FormattedMessage
                id="274c6a4f-e28c-4529-af41-635bfbd3ce24"
                defaultMessage="You cannot send a new transaction because this organization is disabled."
              />
            )
          }
          disabledHintPlacement="bottom"
          fullwidth
          buttonColor="action"
          variant="primary"
          automationId="new-transaction"
        >
          {showPlaceOrderUI ? (
            <FormattedMessage
              id="7d44f0dd-8560-4714-ad84-38163cc0bae8"
              defaultMessage="Place an order"
            />
          ) : (
            <FormattedMessage
              id="274c6a4f-e28c-4529-af41-635bfbd3ce24"
              defaultMessage="Send new transaction"
            />
          )}
          <DashboardDropdownIcon />
        </Button>
      }
    >
      {() => (
        <>
          {showCloseOption && (
            <PopoutMenuMultilineItem
              link
              iconName="address"
              onClick={handleRealEstateOption}
              primaryContent={intl.formatMessage(TRANSACTION_CTA_MESSAGES.closeText)}
              withFixedWidth
              secondaryContent={realClosingContent}
              disabled={loading}
              data-automation-id="new-real-estate-closing"
            />
          )}
          {showNotarizationOption && (
            <PopoutMenuMultilineItem
              link
              withFixedWidth
              iconName="notarize-line"
              onClick={() =>
                createTransaction({
                  configId: OTHER_CONFIG.id,
                })
              }
              primaryContent={intl.formatMessage(TRANSACTION_CTA_MESSAGES.notarizeText)}
              secondaryContent={intl.formatMessage(TRANSACTION_CTA_MESSAGES.realNotarizeSubText)}
              disabled={loading}
              data-automation-id="new-other-transaction"
            />
          )}

          {showEsignOption && (lenderEsignEnabled || isTitlePortal) && (
            <PopoutMenuMultilineItem
              link
              withFixedWidth
              iconName="pen-tip"
              onClick={() =>
                createTransaction({
                  transactionType: ESIGN_TRANSACTION_TYPE,
                  configId: REAL_ESTATE_SIGN_CONFIG.id,
                })
              }
              primaryContent={intl.formatMessage(TRANSACTION_CTA_MESSAGES.signText)}
              secondaryContent={intl.formatMessage(TRANSACTION_CTA_MESSAGES.signSubText)}
              disabled={loading}
              data-automation-id="new-esign-transaction"
            />
          )}
          {showIdentifyOption && (
            <PopoutMenuMultilineItem
              link
              withFixedWidth
              iconName="secure"
              onClick={() =>
                createTransaction({
                  transactionType: IDENTIFY_TRANSACTION_TYPE,
                  configId: REAL_ESTATE_IDENTIFY_CONFIG.id,
                })
              }
              primaryContent={intl.formatMessage(TRANSACTION_CTA_MESSAGES.identifyText)}
              secondaryContent={intl.formatMessage(TRANSACTION_CTA_MESSAGES.identifySubText)}
              disabled={loading}
              data-automation-id="new-identify-transaction"
            />
          )}
          {showVerifyOption && (
            <PopoutMenuMultilineItem
              link
              withFixedWidth
              iconName="video-chat"
              onClick={() =>
                createTransaction({
                  transactionType: VERIFY_TRANSACTION_TYPE,
                  configId: REAL_ESTATE_VERIFY_CONFIG.id,
                })
              }
              primaryContent={intl.formatMessage(TRANSACTION_CTA_MESSAGES.verifyText)}
              secondaryContent={intl.formatMessage(TRANSACTION_CTA_MESSAGES.verifySubText)}
              disabled={loading}
              data-automation-id="new-verify-transaction"
            />
          )}
        </>
      )}
    </PopoutMenu>
  );
}
